import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  User,
  ShieldCheck,
  Activity,
  MessageSquare,
  FileText,
  CheckCircle2,
  XCircle,
  Send as SendIcon,
  ClipboardCheck,
  RotateCcw,
  Lock,
  Megaphone,
} from "lucide-react";
import { toast } from "sonner";

const sb: any = supabase as any;

interface AssessmentMessage {
  id: string;
  author_user_id: string;
  author_role: string;
  author_name: string | null;
  body: string;
  referenced_finding_id: string | null;
  referenced_finding_control: string | null;
  edited_at: string | null;
  created_at: string;
}

interface ActivityEvent {
  source: "assessment" | "cap" | "finding" | "cap_plan";
  event_type: string;
  event_id: string;
  finding_id: string | null;
  finding_control: string | null;
  author_user_id: string;
  author_role: string;
  author_name: string | null;
  message: string;
  created_at: string;
}

const ROLE_META: Record<string, { label: string; className: string; Icon: typeof User }> = {
  reviewer: { label: "Reviewer", className: "bg-primary/10 text-primary", Icon: ShieldCheck },
  admin: { label: "Admin", className: "bg-primary/10 text-primary", Icon: ShieldCheck },
  respondent: { label: "Respondent", className: "bg-blue-500/10 text-blue-700", Icon: User },
  system: { label: "System", className: "bg-muted text-muted-foreground", Icon: MessageSquare },
};

const EVENT_ICON: Record<string, typeof MessageSquare> = {
  message: MessageSquare,
  evidence_request: FileText,
  evidence_fulfilled: CheckCircle2,
  evidence_cancelled: XCircle,
  submitted: SendIcon,
  first_review_completed: ClipboardCheck,
  reviewed: ClipboardCheck,
  closed: Lock,
  validation_validated: CheckCircle2,
  validation_rejected: XCircle,
  validation_needs_changes: RotateCcw,
  validation_approved: CheckCircle2,
  finding_closed: Lock,
  finding_reopened: RotateCcw,
  cap_published: Megaphone,
  cap_republished: Megaphone,
};

interface Props {
  assessmentId: string | null | undefined;
  /** @deprecated retained for back-compat — no longer rendered */
  assessmentLabel?: string | null;
  /** Optional override; the bubble will otherwise resolve org name via RPC. */
  orgName?: string | null;
}

interface Participant {
  user_id: string;
  display_name: string;
  role: string;
}

const UNREAD_POLL_MS = 30000;
const OPEN_POLL_MS = 10000;

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diff = Math.max(0, now - d.getTime());
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  const sameDay = d.toDateString() === new Date().toDateString();
  const hhmm = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (hr < 24 && sameDay) return `Today ${hhmm}`;
  const y = new Date(now - 86400000);
  if (d.toDateString() === y.toDateString()) return `Yesterday ${hhmm}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + ` ${hhmm}`;
}

function RelativeTime({ iso }: { iso: string }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <span title={new Date(iso).toLocaleString()}>{formatRelative(iso)}</span>
  );
}

export const AssessmentCommunicationBubble: React.FC<Props> = ({
  assessmentId,
  orgName: orgNameProp,
}) => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [resolvedOrgName, setResolvedOrgName] = useState<string | null>(null);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"messages" | "activity">("messages");
  const [messages, setMessages] = useState<AssessmentMessage[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const scrollEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setCurrentUserId(data.user?.id ?? null);
    });
    return () => { cancelled = true; };
  }, []);

  const fetchUnread = useCallback(async () => {
    if (!assessmentId) return;
    const { data, error } = await sb.rpc("assessment_messages_unread_count", {
      p_assessment_id: assessmentId,
    });
    if (!error) setUnread(Number(data ?? 0));
  }, [assessmentId]);

  const fetchMessages = useCallback(async () => {
    if (!assessmentId) return;
    const { data, error } = await sb.rpc("assessment_messages_get", {
      p_assessment_id: assessmentId,
    });
    if (error) {
      toast.error(error.message ?? "Failed to load messages");
      return;
    }
    setMessages((data ?? []) as AssessmentMessage[]);
  }, [assessmentId]);

  const fetchActivity = useCallback(async () => {
    if (!assessmentId) return;
    const { data, error } = await sb.rpc("assessment_activity_get", {
      p_assessment_id: assessmentId,
      p_limit: 200,
    });
    if (error) {
      toast.error(error.message ?? "Failed to load activity");
      return;
    }
    setActivity((data ?? []) as ActivityEvent[]);
  }, [assessmentId]);

  // Poll unread only while panel is closed
  useEffect(() => {
    if (!assessmentId || open) return;
    fetchUnread();
    const t = setInterval(fetchUnread, UNREAD_POLL_MS);
    return () => clearInterval(t);
  }, [assessmentId, open, fetchUnread]);

  // When panel opens, load and mark read; poll messages+activity every 10s while open
  useEffect(() => {
    if (!open || !assessmentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([fetchMessages(), fetchActivity()]);
      if (!cancelled) {
        setLoading(false);
        await sb.rpc("assessment_messages_mark_read", { p_assessment_id: assessmentId });
        setUnread(0);
        setTimeout(() => scrollEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
    })();
    const poll = setInterval(() => {
      fetchMessages();
      fetchActivity();
    }, OPEN_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [open, assessmentId, fetchMessages, fetchActivity]);

  const handlePost = async () => {
    const text = draft.trim();
    if (!text || !assessmentId) return;
    setPosting(true);
    const { error } = await sb.rpc("assessment_messages_post", {
      p_assessment_id: assessmentId,
      p_body: text,
      p_referenced_finding_id: null,
    });
    setPosting(false);
    if (error) {
      toast.error(error.message ?? "Failed to post message");
      return;
    }
    setDraft("");
    await Promise.all([fetchMessages(), fetchActivity()]);
    setTimeout(() => scrollEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const handleFindingChip = (findingId: string) => {
    // Best-effort deep-link: emit a custom event consumers can listen for.
    window.dispatchEvent(
      new CustomEvent("cap:open-finding", { detail: { findingId } }),
    );
    toast.message("Finding referenced", {
      description: "Switch to the CAP tab to view the linked finding.",
    });
  };

  // Fetch participants once per assessment
  useEffect(() => {
    if (!assessmentId) return;
    let cancelled = false;
    setParticipants([]);
    setResolvedOrgName(null);
    (async () => {
      const { data, error } = await sb.rpc("assessment_get_participants", {
        p_assessment_id: assessmentId,
      });
      if (cancelled) return;
      if (error) {
        console.warn("assessment_get_participants failed", error);
        return;
      }
      if (!data) return;
      const rows = data as Array<Participant & { org_name: string | null }>;
      setParticipants(
        rows.map((r) => ({ user_id: r.user_id, display_name: r.display_name, role: r.role })),
      );
      const name = rows.find((r) => r.org_name)?.org_name ?? null;
      setResolvedOrgName(name);
    })();
    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  const fallbackParticipants = useMemo(() => {
    if (participants.length > 0) return participants;
    const byKey = new Map<string, Participant>();
    const add = (userId: string | null | undefined, displayName: string | null | undefined, role: string | null | undefined) => {
      if (!userId || !role || role === "system") return;
      const key = `${userId}-${role}`;
      if (byKey.has(key)) return;
      byKey.set(key, {
        user_id: userId,
        display_name: displayName?.trim() || roleLabel(role),
        role,
      });
    };
    messages.forEach((m) => add(m.author_user_id, m.author_name, m.author_role));
    activity.forEach((e) => add(e.author_user_id, e.author_name, e.author_role));
    return Array.from(byKey.values());
  }, [activity, messages, participants]);

  const orgName = orgNameProp || resolvedOrgName;
  const reviewers = useMemo(
    () => dedupeByUser(fallbackParticipants.filter((p) => p.role === "reviewer")),
    [fallbackParticipants],
  );
  const respondents = useMemo(
    () => dedupeByUser(fallbackParticipants.filter((p) => p.role === "respondent")),
    [fallbackParticipants],
  );
  const participantRoleByUserId = useMemo(() => {
    const roles = new Map<string, string>();
    for (const p of fallbackParticipants) {
      if (!p.user_id || !p.role || p.role === "system") continue;
      if (p.role === "respondent" || !roles.has(p.user_id)) roles.set(p.user_id, p.role);
    }
    return roles;
  }, [fallbackParticipants]);

  const resolveSpeakerRole = useCallback(
    (userId: string | null | undefined, rpcRole: string | null | undefined) => {
      if (userId) {
        const participantRole = participantRoleByUserId.get(userId);
        if (participantRole) return participantRole;
      }
      return normalizeRole(rpcRole);
    },
    [participantRoleByUserId],
  );

  const headerTitle = orgName ? `${orgName} Assessment` : "Assessment Communication";

  if (!assessmentId) return null;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90 transition flex items-center justify-center"
          aria-label="Open assessment communication"
        >
          <MessageCircle className="h-6 w-6" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-2rem)] rounded-xl border bg-card shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b bg-muted/40">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  <MessageCircle className="h-4 w-4 text-primary shrink-0" />
                  <span className="truncate">{headerTitle}</span>
                </div>
                <ParticipantsLine
                  reviewers={reviewers}
                  respondents={respondents}
                  currentUserId={currentUserId}
                  expanded={participantsOpen}
                  onToggle={() => setParticipantsOpen((v) => !v)}
                />
              </div>
              <Button size="icon" variant="ghost" onClick={() => setOpen(false)} aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            {/* Tabs */}
            <div className="mt-3 flex items-center gap-1 border-b -mb-3">
              <TabButton active={tab === "messages"} onClick={() => setTab("messages")}>
                <MessageSquare className="h-3.5 w-3.5" /> Messages
              </TabButton>
              <TabButton active={tab === "activity"} onClick={() => setTab("activity")}>
                <Activity className="h-3.5 w-3.5" /> All Activity
              </TabButton>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {loading ? (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            ) : tab === "messages" ? (
              <ScrollArea className="h-full">
                <div className="p-3 space-y-2">
                  {messages.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic text-center py-6">
                      No assessment-level messages yet. Use the box below to start the conversation.
                    </p>
                  ) : (
                    messages.map((m) => (
                      <MessageRow
                        key={m.id}
                        message={m}
                        displayRole={resolveSpeakerRole(m.author_user_id, m.author_role)}
                        isOwn={!!currentUserId && m.author_user_id === currentUserId}
                        onFindingClick={handleFindingChip}
                      />
                    ))
                  )}
                  <div ref={scrollEndRef} />
                </div>
              </ScrollArea>
            ) : (
              <ScrollArea className="h-full">
                <div className="p-3 space-y-2">
                  {activity.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic text-center py-6">
                      No activity yet.
                    </p>
                  ) : (
                    activity.map((e) => (
                      <ActivityRow
                        key={`${e.source}-${e.event_id}`}
                        event={e}
                        displayRole={resolveSpeakerRole(e.author_user_id, e.author_role)}
                        onFindingClick={handleFindingChip}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Composer (messages tab only) */}
          {tab === "messages" && (
            <div className="border-t p-3 space-y-2 bg-background">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Send an assessment-level message…"
                className="min-h-[60px] text-sm"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground italic">
                  Visible to all reviewers and respondents on this assessment
                </span>
                <Button size="sm" onClick={handlePost} disabled={posting || !draft.trim()}>
                  {posting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Send className="h-3.5 w-3.5 mr-1" />
                  )}
                  Send
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

function dedupeByUser(list: Participant[]): Participant[] {
  const seen = new Set<string>();
  const out: Participant[] = [];
  for (const p of list) {
    if (seen.has(p.user_id)) continue;
    seen.add(p.user_id);
    out.push(p);
  }
  return out;
}

function ParticipantsLine({
  reviewers,
  respondents,
  currentUserId,
  expanded,
  onToggle,
}: {
  reviewers: Participant[];
  respondents: Participant[];
  currentUserId: string | null;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (reviewers.length === 0 && respondents.length === 0) {
    return (
      <div className="text-[11px] text-muted-foreground mt-0.5">No participants assigned</div>
    );
  }

  // Show only the "other party" in the header, like Teams/Slack DMs.
  const viewerIsReviewer = !!currentUserId && reviewers.some((r) => r.user_id === currentUserId);
  const viewerIsRespondent = !!currentUserId && respondents.some((r) => r.user_id === currentUserId);

  let shownReviewers = reviewers;
  let shownRespondents = respondents;
  if (viewerIsReviewer && !viewerIsRespondent) {
    shownReviewers = [];
  } else if (viewerIsRespondent && !viewerIsReviewer) {
    shownRespondents = [];
  }

  const reviewerLine =
    shownReviewers.length === 0
      ? null
      : shownReviewers.length === 1
      ? `Reviewer: ${shownReviewers[0].display_name}`
      : `Reviewers (${shownReviewers.length})`;

  const collapseRespondents = shownRespondents.length > 2;
  const respondentLine =
    shownRespondents.length === 0
      ? null
      : collapseRespondents && !expanded
      ? `Respondents (${shownRespondents.length})`
      : `${shownRespondents.length === 1 ? "Respondent" : "Respondents"}: ${shownRespondents
          .map((r) => r.display_name)
          .join(", ")}`;

  const canToggle = collapseRespondents || shownReviewers.length > 1;

  return (
    <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight space-y-0.5">
      {reviewerLine && <div className="truncate">{reviewerLine}</div>}
      {respondentLine && <div className="truncate">{respondentLine}</div>}
      {canToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="text-[10px] text-primary hover:underline"
        >
          {expanded ? "Hide participants" : "Show all participants"}
        </button>
      )}
      {expanded && (
        <div className="mt-1 rounded-md border bg-background p-2 space-y-1">
          {reviewers.map((r) => (
            <div key={`r-${r.user_id}`} className="text-[11px]">
              <span className="font-medium">{r.display_name}</span>
              <span className="text-muted-foreground"> · Reviewer</span>
            </div>
          ))}
          {respondents.map((r) => (
            <div key={`p-${r.user_id}`} className="text-[11px]">
              <span className="font-medium">{r.display_name}</span>
              <span className="text-muted-foreground"> · Respondent</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium border-b-2 transition flex items-center gap-1 ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function RoleBadge({ role }: { role: string }) {
  const meta = ROLE_META[role] ?? ROLE_META.system;
  const Icon = meta.Icon;
  return (
    <Badge variant="outline" className={`text-[10px] gap-1 ${meta.className}`}>
      <Icon className="h-2.5 w-2.5" /> {meta.label}
    </Badge>
  );
}

function roleLabel(role: string) {
  return ROLE_META[role]?.label ?? "User";
}

function normalizeRole(role: string | null | undefined) {
  const normalized = String(role ?? "").toLowerCase().trim();
  return ROLE_META[normalized] ? normalized : "system";
}

function MessageRow({
  message,
  displayRole,
  isOwn,
  onFindingClick,
}: {
  message: AssessmentMessage;
  displayRole: string;
  isOwn: boolean;
  onFindingClick: (id: string) => void;
}) {
  return (
    <div
      className={`rounded-md border px-3 py-2 max-w-[85%] ${
        isOwn ? "ml-auto bg-primary/10 border-primary/20" : "mr-auto bg-card"
      }`}
    >
      <div className={`flex items-center gap-2 mb-1 flex-wrap ${isOwn ? "justify-end" : ""}`}>
        <RoleBadge role={displayRole} />
        {message.author_name && (
          <span className="text-[11px] font-medium text-foreground">
            {isOwn ? "You" : message.author_name}
          </span>
        )}
        <span className="text-[10px] text-muted-foreground">
          <RelativeTime iso={message.created_at} />
        </span>
        {message.referenced_finding_id && (
          <button
            type="button"
            onClick={() => onFindingClick(message.referenced_finding_id!)}
            className={isOwn ? "" : "ml-auto"}
          >
            <Badge variant="secondary" className="text-[10px] cursor-pointer hover:bg-secondary/80">
              {message.referenced_finding_control ?? "Finding"}
            </Badge>
          </button>
        )}
      </div>
      <p className={`text-xs whitespace-pre-wrap text-foreground ${isOwn ? "text-right" : ""}`}>
        {message.body}
      </p>
    </div>
  );
}

function resolveVerb(event: ActivityEvent): string {
  if (event.source === "assessment") {
    switch (event.event_type) {
      case "message": return "posted an assessment message";
      case "submitted": return "submitted the assessment for review";
      case "first_review_completed": return "completed first review";
      case "reviewed": return "completed review";
      case "closed": return "closed the assessment";
      default: return `assessment ${event.event_type.replace(/_/g, " ")}`;
    }
  }
  if (event.source === "finding") {
    switch (event.event_type) {
      case "validation_validated":
      case "validation_approved":
        return "validated finding";
      case "validation_rejected": return "rejected finding";
      case "validation_needs_changes": return "requested changes on finding";
      case "finding_closed": return "closed finding";
      case "finding_reopened": return "reopened finding";
      default: return event.event_type.replace(/^validation_/, "marked finding ").replace(/_/g, " ");
    }
  }
  if (event.source === "cap_plan") {
    return event.event_type === "cap_republished" ? "republished the CAP" : "published the CAP";
  }
  // cap thread messages
  switch (event.event_type) {
    case "evidence_request": return "requested evidence";
    case "evidence_fulfilled": return "marked evidence fulfilled";
    case "evidence_cancelled": return "cancelled an evidence request";
    default: return "posted a CAP message";
  }
}

function ActivityRow({
  event,
  displayRole,
  onFindingClick,
}: {
  event: ActivityEvent;
  displayRole: string;
  onFindingClick: (id: string) => void;
}) {
  const Icon = EVENT_ICON[event.event_type] ?? MessageSquare;
  const verb = resolveVerb(event);

  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <RoleBadge role={displayRole} />
        <span className="text-[11px] font-medium text-foreground">{event.author_name ?? "User"}</span>
        <span className="text-[11px] text-muted-foreground">{verb}</span>
        {event.finding_id && (
          <button type="button" onClick={() => onFindingClick(event.finding_id!)}>
            <Badge variant="secondary" className="text-[10px] cursor-pointer hover:bg-secondary/80">
              {event.finding_control ?? "Finding"}
            </Badge>
          </button>
        )}
      </div>
      {event.message && (
        <p className="text-xs whitespace-pre-wrap text-foreground line-clamp-3">{event.message}</p>
      )}
      <div className="text-[10px] text-muted-foreground mt-1">
        <RelativeTime iso={event.created_at} />
      </div>
    </div>
  );
}

export default AssessmentCommunicationBubble;
